import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { useAverageSensorData, useSensorReadings, useSubstationSensors } from "@core/apis/shared";
import { SolidCard } from "@components";

import { buildDiagramOverlays, buildIndoorTemperatureSummary } from "./heatingDiagram.types";
import {
  getHeatingDiagramSensorConfigs,
  HEATING_DIAGRAM_SENSOR_TYPES,
} from "./heatingDiagramConfig";
import { HeatingSystemDiagram } from "./HeatingSystemDiagram";

type HeatingSystemSectionProps = Readonly<{
  substationId: string;
}>;

const dateRange = {
  startDate: DateTime.now().minus({ days: 1 }).startOf("day"),
  endDate: DateTime.now().endOf("hour"),
};

export function HeatingSystemSection({ substationId }: HeatingSystemSectionProps) {
  const { t } = useTranslation(["propai_properties", "_common"]);

  const sensorConfigs = useMemo(() => getHeatingDiagramSensorConfigs(t), [t]);

  // Fetch diagram sensors + readings
  const { sensors, isLoading: sensorsLoading } = useSubstationSensors(
    substationId,
    HEATING_DIAGRAM_SENSOR_TYPES
  );

  const sensorUids = useMemo(() => sensors.map((s) => s.uid), [sensors]);

  const { data: sensorData, isLoading: readingsLoading } = useSensorReadings(
    substationId,
    sensorUids,
    dateRange
  );

  // Fetch indoor sensors + average temperature
  const { sensors: indoorSensors } = useSubstationSensors(substationId, ["living_air_temperature"]);

  const { data: averageData } = useAverageSensorData(
    substationId,
    "living_air_temperature",
    dateRange
  );

  const isLoading = sensorsLoading || readingsLoading;

  const overlays = useMemo(() => {
    if (isLoading || sensors.length === 0) return [];
    return buildDiagramOverlays(sensors, sensorData, sensorConfigs);
  }, [sensors, sensorData, sensorConfigs, isLoading]);

  const indoorTemperature = useMemo(
    () => buildIndoorTemperatureSummary(indoorSensors, averageData),
    [indoorSensors, averageData]
  );

  return (
    <SolidCard title={t("heating_system")}>
      <HeatingSystemDiagram
        overlays={overlays}
        indoorTemperature={indoorTemperature}
        isLoading={isLoading}
      />
    </SolidCard>
  );
}
